/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { service } from '@ember/service';
import { TextOptions, TextTypes, FABRIC_FILL_HEX_LENGTH } from 'eflex/constants/work-instructions/tool-props';
import { hexOpacityToDec, decOpacityToHex } from 'eflex/util/opacity-helper';
import { isPresent } from '@ember/utils';
import { all, task, waitForProperty, waitForQueue, timeout } from 'ember-concurrency';
import * as Fabric from 'fabric';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { waitFor } from '@ember/test-waiters';
import { pluck, map, uniq } from 'ramda';
import { intoArray } from '@eflexsystems/ramda-helpers';
import { updateObjectProperty } from 'eflex/util/fabric-helpers';
import getDelayTime from 'eflex/util/get-delay-time';

const UPDATE_ON_EVENTS = new Set(['Backspace', 'Enter']);

const TEXT_TYPES = new Set([
  TextTypes.TEXT,
  TextTypes.ITEXT,
  TextTypes.TEXTBOX,
]);

export default class WorkInstructionEditorToolPropertiesText extends Component {
  @service imageEditor;
  @service systemConfig;
  @service store;

  fontFamilies = TextOptions.fontFamilies;
  fontTypography = TextOptions.fontTypography;
  fontFamilyDefault = TextOptions.defaultFont;
  fontSizeDefault = 36;
  fontStyleDefault = 'normal';
  fontWeightDefault = 'normal';
  fillDefault = '#000000';
  underlineDefault = false;
  linethroughDefault = false;
  textBackgroundColorDefault = '#FFFFFFFF';
  strokeDefault = '#00000000';
  strokeWidthDefault = 2;
  textBackgroundColorOpacityDefault = 100;
  strokeOpacityDefault = 0;

  @tracked selectedTextBackgroundColor = '#FFFFFF';
  @tracked selectedTextBackgroundOpacity = 0;
  @tracked selectedStrokeColor = '#000000';
  @tracked selectedStrokeOpacity = 0;
  @tracked selectedBackgroundColor = '#FFFFFF';
  @tracked selectedBackgroundOpacity = 100;
  @tracked currentTextBox;
  @tracked eventTextBox;
  @tracked textSelection;
  @tracked link = '';
  @tracked startHidden = false;
  @tracked dynamicObjectName;
  @tracked calculatedBackgroundColor = '#FFFFFFFF';
  @tracked calculatedTextBackgroundColor = '#FFFFFFFF';
  @tracked calculatedStrokeColor = '#00000000';
  @tracked selectedStrokeWidth = 2;
  @tracked isUnderline = false;
  @tracked selectedFontStyle = 'normal';
  @tracked isStrikethrough = false;
  @tracked selectedFontWeight = 'normal';
  @tracked selectedFontFamily = TextOptions.defaultFont;
  @tracked selectedTextAlign = 'left';
  @tracked selectedFontSize = 36;
  @tracked selectedTextColor = '#000000';
  @tracked fontDecoration;

  #hasListeners = false;

  // jscpd:ignore-start
  onDidInsert = task(waitFor(async () => {
    await all([
      waitForProperty(this.imageEditor, 'canvas'),
      waitForQueue('afterRender'),
    ]);

    if (this._activeObject() != null) {
      this.updateSelectedProperties();
    } else {
      this.#updateFromSystemConfig();
    }

    if (this.isUnderline) {
      this.fontDecoration = 'underline';
    } else if (this.isStrikethrough) {
      this.fontDecoration = 'strikethrough';
    } else {
      this.fontDecoration = 'none';
    }

    this.imageEditor
      .on('selection:updated', this.updateSelectedProperties)
      .on('selection:cleared', this.#updateFromSystemConfig);

    this.#hasListeners = true;

    const canvas = this.imageEditor.canvas;

    canvas.on('mouse:down', this.#startDraw);
    canvas.on('mouse:move', this.#updateDraw);
    canvas.on('mouse:up', this.#endDraw);
  }));
  // jscpd:ignore-end

  _isText(obj) {
    return TEXT_TYPES.has(obj?.type);
  }

  _activeObject() {
    const obj = this.imageEditor.canvas?.getActiveObject();
    if (this._isText(obj)) {
      return obj;
    } else {
      return null;
    }
  }

  get selectedFontTypographyOption() {
    return this.fontTypography.find(
      (opt) => opt.weight === this.selectedFontWeight && opt.style === this.selectedFontStyle,
    );
  }

  updateFontDecoration = (selected) => {
    this.fontDecoration = selected;

    this.updateIsUnderline(selected === 'underline');
    this.updateIsStrikethrough(selected === 'strikethrough');
  };

  addTextBoxListener() {
    const { eventTextBox } = this;

    eventTextBox.on('selection:changed', this.#updateSelection);
    eventTextBox.on('editing:entered', this.#resetTextSelection);
    eventTextBox.on('editing:exited', this.#resetTextSelection);

    this.eventTextBox.onKeyUp = this.#handleInput;
  }

  removeTextBoxListener() {
    const { eventTextBox } = this;

    eventTextBox.off('selection:changed');
    eventTextBox.off('editing:entered');
    eventTextBox.off('editing:exited');

    this.eventTextBox.onKeyUp = null;
  }

  #handleInput = ({ key }) => {
    if (UPDATE_ON_EVENTS.has(key)) {
      this.#updateSelection();
    }
  };

  #updateSelection = () => {
    const { selectionStart, selectionEnd } = this.eventTextBox;
    const leftBoundary = this.eventTextBox.findLineBoundaryLeft(selectionStart);
    const rightBoundary = this.eventTextBox.findLineBoundaryRight(selectionEnd);

    if (selectionEnd - selectionStart > 0) {
      this.textSelection = [selectionStart, selectionEnd];
    } else if (selectionStart === leftBoundary) {
      this.textSelection = [selectionStart, selectionEnd + 1];
    } else if (selectionEnd === rightBoundary) {
      this.textSelection = [selectionEnd - 1, selectionEnd];
    } else {
      this.textSelection = [selectionStart - 1, selectionEnd];
    }

    this.setPropertiesPanel(this.eventTextBox, this.textSelection);
  };

  resetProperties() {
    Object.assign(this, {
      dynamicObjectName: null,
      link: '',
    });
  }

  #updateFromSystemConfig = () => {
    this.resetProperties();
    const config = this.systemConfig.getWieDefaults('text');

    const backgroundOpacity = config.background?.opacity ?? 0;
    const textBackgroundOpacity = config.textBackground?.opacity ?? 0;
    const strokeOpacity = config.stroke?.opacity ?? 0;

    const calculatedBackground = config.background?.color + decOpacityToHex(backgroundOpacity);
    const calculatedTextBackground = config.textBackground?.color + decOpacityToHex(textBackgroundOpacity);
    const calculatedStrokeColor = config.stroke?.color + decOpacityToHex(strokeOpacity);

    Object.assign(this, {
      selectedFontFamily: config.font?.family,
      selectedFontSize: config.font?.size,
      selectedFontStyle: config.font?.style,
      selectedFontWeight: config.font?.weight,
      selectedTextAlign: config.alignment,
      selectedTextColor: config.font?.color,
      selectedBackgroundColor: config.background?.color,
      selectedBackgroundOpacity: backgroundOpacity,
      selectedTextBackgroundColor: config.textBackground?.color,
      selectedTextBackgroundOpacity: textBackgroundOpacity,
      selectedStrokeColor: config.stroke?.color,
      selectedStrokeOpacity: strokeOpacity,
      selectedStrokeWidth: config.stroke?.width,
      isUnderline: config.font?.underline,
      isStrikethrough: config.font?.strikethrough,
      calculatedBackgroundColor: calculatedBackground,
      calculatedTextBackgroundColor: calculatedTextBackground,
      calculatedStrokeColor,
    });
  };

  @action
  updateSelectedProperties() {
    const obj = this._activeObject();
    if (obj == null) {
      return;
    }

    const { eventTextBox } = this;

    if (eventTextBox != null && eventTextBox !== obj) {
      this.removeTextBoxListener();
    }

    this.eventTextBox = obj;
    this.setPropertiesPanel(obj, null);

    if (eventTextBox !== obj) {
      this.addTextBoxListener();
    }
  }

  setPropertiesPanel(obj, range = null) {
    if (this.currentTextBox != null) {
      return;
    }

    if (range == null) {
      range = [0, obj.text.length];
    }

    if (range[1] === 0) {
      range[1] = 1;
    }

    const stroke = this.calculateStyleValue(obj, 'stroke', range) || this.strokeDefault;

    const textBackground =
      this.calculateStyleValue(obj, 'textBackgroundColor', range) || this.textBackgroundColorDefault;

    const selectedTextColor = this.calculateStyleValue(obj, 'fill', range) || this.fillDefault;

    const selectedStrokeColor = stroke?.slice(0, FABRIC_FILL_HEX_LENGTH);
    const selectedTextBackgroundColor = textBackground?.slice(0, FABRIC_FILL_HEX_LENGTH);
    const selectedBackgroundColor = obj.backgroundColor?.slice(0, FABRIC_FILL_HEX_LENGTH);

    const selectedStrokeOpacity = this.calculateOpacityValue(obj, 'stroke', range);

    const selectedTextBackgroundOpacity = this.calculateOpacityValue(obj, 'textBackgroundColor', range);

    const selectedBackgroundOpacity = hexOpacityToDec(obj.backgroundColor?.slice(FABRIC_FILL_HEX_LENGTH));

    Object.assign(this, {
      selectedFontFamily: this.calculateStyleValue(obj, 'fontFamily', range),
      selectedFontSize: this.calculateStyleValue(obj, 'fontSize', range),
      selectedFontStyle: this.calculateStyleValue(obj, 'fontStyle', range),
      selectedFontWeight: this.calculateStyleValue(obj, 'fontWeight', range),
      isUnderline: this.calculateStyleValue(obj, 'underline', range),
      isStrikethrough: this.calculateStyleValue(obj, 'linethrough', range),
      selectedStrokeWidth: this.calculateStyleValue(obj, 'strokeWidth', range),

      calculatedStrokeColor: stroke,
      calculatedTextBackgroundColor: textBackground,
      selectedTextColor,
      selectedTextBackgroundColor,
      selectedTextBackgroundOpacity,
      selectedStrokeColor,
      selectedStrokeOpacity,
      selectedBackgroundOpacity,
      selectedBackgroundColor,
      selectedTextAlign: obj.textAlign,
      dynamicObjectName: obj.eflex?.dynamicObjectName,
      startHidden: obj.eflex?.startHidden,
      link: obj.eflex?.link,
      calculatedBackgroundColor: obj.backgroundColor,
    });
  }

  _value(collection, defaultValue) {
    if (collection.length > 1) {
      return null;
    }

    if (isPresent(collection[0])) {
      return collection[0];
    }

    return defaultValue;
  }

  calculateOpacityValue(obj, style, range) {
    const styles = obj.getSelectionStyles(range[0], range[1], true);
    const collection = intoArray(
      map(elem => hexOpacityToDec(elem[style]?.slice(FABRIC_FILL_HEX_LENGTH))),
      uniq,
    )(styles);

    return this._value(collection, this[`${style}OpacityDefault`]);
  }

  calculateStyleValue(obj, style, range) {
    const styles = obj.getSelectionStyles(range[0], range[1], true);
    const collection = intoArray(
      pluck(style),
      uniq,
    )(styles);

    return this._value(collection, this[`${style}Default`]);
  }

  #resetTextSelection = () => {
    this.textSelection = null;
  };

  #startDraw = ({ e, target }) => {
    const { canvas } = this.imageEditor;

    if (target?.isEditing || target?.type === 'textbox' || !canvas.isTextMode) {
      return;
    }

    this.imageEditor.disableHistory();

    const pointer = canvas.getScenePoint(e);
    const textBox = new Fabric.Textbox('', {
      left: pointer.x,
      top: pointer.y,
      paintFirst: 'stroke',
      lockScalingY: true,
      backgroundColor: this.calculatedBackgroundColor,
      textAlign: this.selectedTextAlign,
      fill: this.selectedTextColor,
      fontFamily: this.selectedFontFamily,
      fontSize: this.selectedFontSize,
      fontStyle: this.selectedFontStyle,
      fontWeight: this.selectedFontWeight,
      underline: this.isUnderline,
      linethrough: this.isStrikethrough,
      textBackgroundColor: this.calculatedTextBackgroundColor,
      stroke: this.calculatedStrokeColor,
      strokeWidth: this.selectedStrokeWidth,
      eflex: {
        dynamicObjectName: this.dynamicObjectName,
        startHidden: this.startHidden,
        link: this.link,
      },
    });

    textBox.setControlsVisibility({
      bl: false,
      br: false,
      mb: false,
      mt: false,
      tl: false,
      tr: false,
    });

    canvas.add(textBox);
    textBox.setCoords();
    canvas.setActiveObject(textBox);
    this.currentTextBox = textBox;
    canvas.renderAll();
  };

  #updateDraw = ({ e }) => {
    if (this.currentTextBox == null) {
      return;
    }
    const { canvas } = this.imageEditor;
    const pointer = canvas.getScenePoint(e);
    this.currentTextBox.set({ width: pointer.x - this.currentTextBox.left });
    this.currentTextBox.setCoords();
    canvas.renderAll();
  };

  #endDraw = () => {
    if (this.currentTextBox == null) {
      return;
    }
    const { canvas } = this.imageEditor;

    this.currentTextBox.set({ padding: 8 });
    this.currentTextBox.insertChars(TextOptions.defaultText);
    this.imageEditor.enableHistory();
    canvas.fire('object:modified', { target: this.currentTextBox });

    const styles = {
      fill: this.selectedTextColor,
      fontFamily: this.selectedFontFamily,
      fontSize: this.selectedFontSize,
      fontStyle: this.selectedFontStyle,
      fontWeight: this.selectedFontWeight,
      underline: this.isUnderline,
      linethrough: this.isStrikethrough,
      textBackgroundColor: this.calculatedTextBackgroundColor,
      stroke: this.calculatedStrokeColor,
      strokeWidth: this.selectedStrokeWidth,
    };

    this.currentTextBox.setSelectionStyles(styles, 0, TextOptions.defaultText.length);

    canvas.renderAll();
    this.args.objectCreated?.(this.currentTextBox);
    this.currentTextBox = null;

    this.updateSelectedProperties();
    this.eventTextBox.enterEditing();
    this.eventTextBox.selectAll();
  };

  willDestroy() {
    super.willDestroy(...arguments);
    if (this.eventTextBox != null) {
      this.removeTextBoxListener();
    }

    const imageEditor = this.imageEditor;

    if (this.#hasListeners) {
      imageEditor
        .off('selection:updated', this.updateSelectedProperties)
        .off('selection:cleared', this.#updateFromSystemConfig);
    }

    const canvas = imageEditor.canvas;

    if (canvas) {
      [
        'mouse:down',
        'mouse:move',
        'mouse:up',
      ].forEach(removedEvent => canvas.off(removedEvent));
    }
  }

  updateTextRange(prop, val) {
    const obj = this._activeObject();
    if (obj == null) {
      return;
    }

    const styles = { [prop]: val };
    const range = this.textSelection ?? [0, obj.text.length];
    this.imageEditor.updateTextRange(obj, styles, range);
  }

  @action
  updateLink(val) {
    this.link = val;
    const obj = this._activeObject();
    if (!obj) {
      return;
    }
    obj.eflex ??= {};
    updateObjectProperty(obj.eflex, 'link', val);
    this.imageEditor.notifyModified(obj);
  }

  @action
  updateDynamicObjectName(val) {
    this.dynamicObjectName = val;
    const obj = this._activeObject();
    if (!obj) {
      return;
    }
    obj.eflex ??= {};
    updateObjectProperty(obj.eflex, 'dynamicObjectName', val);
    this.imageEditor.notifyModified(obj);
  }

  @action
  updateStartHidden(val) {
    this.startHidden = val;
    const obj = this._activeObject();
    if (!obj) {
      return;
    }
    obj.eflex ??= {};
    updateObjectProperty(obj.eflex, 'startHidden', val);
    this.imageEditor.notifyModified(obj);
  }

  onUpdateStrokeColor = task({ restartable: true }, waitFor(async (color, opacity, combinedColor) => {
    Object.assign(this, {
      selectedStrokeColor: color,
      calculatedStrokeColor: combinedColor,
      selectedStrokeOpacity: opacity,
    });

    await timeout(getDelayTime(50));

    this.updateTextRange('stroke', combinedColor);
  }));

  onUpdateTextBackground = task({ restartable: true }, waitFor(async (color, opacity, combinedColor) => {
    Object.assign(this, {
      selectedTextBackgroundColor: color,
      calculatedTextBackgroundColor: combinedColor,
      selectedTextBackgroundOpacity: opacity,
    });

    await timeout(getDelayTime(50));

    this.updateTextRange('textBackgroundColor', combinedColor);
  }));

  onUpdateBackgroundColor = task({ restartable: true }, waitFor(async (color, opacity, combinedColor) => {
    Object.assign(this, {
      selectedBackgroundColor: color,
      selectedBackgroundOpacity: opacity,
      calculatedBackgroundColor: combinedColor,
    });

    await timeout(getDelayTime(50));

    const obj = this._activeObject();
    updateObjectProperty(obj, 'backgroundColor', combinedColor);
    this.imageEditor.notifyModified(obj);
  }));

  @action
  updateStrokeWidth(val) {
    this.selectedStrokeWidth = val;
    this.updateTextRange('strokeWidth', val);
  }

  @action
  updateIsUnderline(forceState = !this.isUnderline) {
    this.isUnderline = forceState;
    this.updateTextRange('underline', this.isUnderline);
  }

  @action
  updateIsStrikethrough(forceState = !this.isStrikethrough) {
    this.isStrikethrough = forceState;
    this.updateTextRange('linethrough', this.isStrikethrough);
  }

  @action
  updateFontTypography(option) {
    if (option.weight !== this.selectedFontWeight) {
      this.selectedFontWeight = option.weight;
      this.updateTextRange('fontWeight', option.weight);
    }

    if (option.style !== this.selectedFontStyle) {
      this.selectedFontStyle = option.style;
      this.updateTextRange('fontStyle', option.style);
    }
  }

  @action
  updateFontFamily(val) {
    this.selectedFontFamily = val;
    this.updateTextRange('fontFamily', val);
  }

  @action
  updateTextAlign(val) {
    this.selectedTextAlign = val;
    const obj = this._activeObject();
    updateObjectProperty(obj, 'textAlign', val);
    this.imageEditor.notifyModified(obj);
  }

  @action
  updateFontSize(val) {
    this.selectedFontSize = val;
    this.updateTextRange('fontSize', val);
  }

  onUpdateTextColor = task({ restartable: true }, waitFor(async (val) => {
    this.selectedTextColor = val;

    await timeout(getDelayTime(50));

    this.updateTextRange('fill', val);
  }));

  @action
  onMakeDefault() {
    const textDefault = this.store.createRecord('wie-config/text', {
      alignment: this.selectedTextAlign,
      font: this.store.createRecord('wie-config/font', {
        family: this.selectedFontFamily,
        weight: this.selectedFontWeight,
        size: this.selectedFontSize,
        style: this.selectedFontStyle,
        color: this.selectedTextColor,
        underline: this.isUnderline,
        strikethrough: this.isStrikethrough,
      }),
      stroke: this.store.createRecord('wie-config/stroke', {
        color: this.selectedStrokeColor,
        opacity: this.selectedStrokeOpacity,
        width: this.selectedStrokeWidth,
      }),
      background: this.store.createRecord('wie-config/fill', {
        color: this.selectedBackgroundColor,
        opacity: this.selectedBackgroundOpacity,
      }),
      textBackground: this.store.createRecord('wie-config/fill', {
        color: this.selectedTextBackgroundColor,
        opacity: this.selectedTextBackgroundOpacity,
      }),
    });

    this.systemConfig.config.wie.editorDefaults.text = textDefault;
    return this.args.saveDefault();
  }
}
